import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import DataSources from "./pages/DataSources";
import News from "./pages/News";
import TrainingResources from "./pages/TrainingResources";
import ChatPage from "./pages/ChatPage";
import Sidebar from "./components/Sidebar";
import useChat from "./hooks/useChat";
import "./App.css";
import "./EmbedApp.css";
import "./components/Footer.css";
import DisclaimerModal from "./components/DisclaimerModal";
import DisclaimerNotice from "./components/DisclaimerNotice";
import EmbedThemeInit from "./components/EmbedThemeInit";
import { PanelLeft } from "lucide-react";

export default function EmbedApp() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [currentConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const resizeObserverLoopErr = (e) => {
      if (e.message.includes("ResizeObserver loop completed with undelivered notifications")) {
        const resizeObserverErrDiv = document.getElementById("webpack-dev-server-client-overlay-div");
        const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    };
    window.addEventListener("error", resizeObserverLoopErr);
    return () => window.removeEventListener("error", resizeObserverLoopErr);
  }, []);

  const chat = useChat({
    getToken: async () => null,
    isSignedIn: true,
    openSignIn: () => {},
    currentConversationId: null,
    fetchConversations: async () => {},
    hasSubscription: true,
    trialExpired: false,
    setShowPaywall: () => {},
    embedMode: true,
  });

  const handleNewChat = () => {
    chat.handleNewChat();
    navigate("/");
  };

  const noop = () => {};

  return (
    <>
      <EmbedThemeInit />
      <div className={`app-layout embed-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <nav aria-label="Skip links">
          <a
            href="#main-content"
            className="skip-nav"
            aria-label="Skip to main content"
            onClick={(e) => {
              const mainEl = document.getElementById("main-content");
              if (!mainEl) return;
              e.preventDefault();
              mainEl.focus();
              mainEl.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Skip to main content
          </a>
        </nav>
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          handleNewChat={handleNewChat}
          conversations={[]}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentConversationId={currentConversationId}
          loadConversation={noop}
          hasSubscription={true}
          isSuperUser={false}
          handleSubscribe={noop}
          isCheckoutLoading={false}
          handleManageSubscription={noop}
          isPortalLoading={false}
          deleteConversation={noop}
          onOpenDisclaimer={() => setShowDisclaimerModal(true)}
          isEmbedMode={true}
        />

        <main
          id="main-content"
          tabIndex={-1}
          className={`main-content ${sidebarOpen ? '' : 'sidebar-closed'}`}
        >
          {!sidebarOpen && (
            <button className="toggle-btn floating desktop-hidden" onClick={() => setSidebarOpen(true)} title="Open sidebar" aria-label="Open sidebar">
              <PanelLeft size={18} aria-hidden="true" />
            </button>
          )}
          <Routes>
            <Route path="/sources" element={<DataSources />} />
            <Route path="/news" element={<News />} />
            <Route path="/training" element={<TrainingResources />} />
            <Route path="*" element={
              <ChatPage
                chat={chat}
                showPaywall={false}
                setShowPaywall={noop}
                handleSubscribe={noop}
                isCheckoutLoading={false}
                currentConversationId={null}
                hasSubscription={true}
                trialExpired={false}
                loadConversation={noop}
                handleNewChat={handleNewChat}
                embedMode={true}
              />
            } />
          </Routes>

          <footer className="app-footer">
            <DisclaimerNotice onOpenFull={() => setShowDisclaimerModal(true)} />
            <div className="footer-branding">
              <img src={`${process.env.PUBLIC_URL}/revelo.png`} alt="Revelo" className="revelo-logo" />
              <span className="copyright-text">© 2026 iAccessible | Powered by Revelo</span>
            </div>
          </footer>
        </main>
      </div>

      <DisclaimerModal
        isVisible={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
      />
    </>
  );
}
